import { Link } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { BusinessRelationshipDetail } from '@/entities/business-relationship/api/businessRelationship.api';
import {
  useBusinessRelationshipDetail,
  useBusinessRelationshipList,
  usePrescriptionRegistrations,
} from '@/entities/business-relationship/hooks/useBusinessRelationships';
import {
  type RelationshipGroup,
  getRelationshipGroup,
  getStatusBadgeTone,
} from '@/entities/business-relationship/lib/statusView';
import {
  getUploadRegistrationYearMonth,
  getUploadSettlementYearMonth,
} from '@/entities/prescription/lib/uploadSettlementMonth';
import { ErrorState, LoadingBlock } from '@/shared/components/StateViews';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card } from '@/shared/components/ui/card';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { formatShortYm } from '@/shared/utils/format';
import { useUIStore } from '@/stores/uiStore';

import {
  AddressDialog,
  BusinessLicenseDialog,
  PharmacyDialog,
} from './ClinicHeaderOverlays';
import { copyWithToast } from '../utils';
import { MissedDrugsDrawer } from './MissedDrugsDrawer';
import { PrescriptionHistoryCard } from './PrescriptionHistoryCard';
import { TradeInfoCard } from './TradeInfoCard';
import { TradeInfoDrawer } from './TradeInfoDrawer';

type HeaderOverlay = 'address' | 'license' | 'pharmacy' | null;

const ClinicHeader = ({
  detail,
  onReload,
}: {
  detail: BusinessRelationshipDetail;
  onReload: () => void;
}) => {
  const clinic = detail;
  const { pharmacies } = detail;
  const [overlay, setOverlay] = useState<HeaderOverlay>(null);
  const hospital = detail.tradeUnit === 'DRUG';
  const ongoing = detail.tradeStatusCounts.ongoing;
  const meta = [
    [
      clinic.clinicTypeCodeName ?? (hospital ? '병원' : '의원'),
      clinic.ykiho && `요양기관번호 ${clinic.ykiho}`,
    ]
      .filter(Boolean)
      .join(' · '),
    hospital ? `거래 중 의약품 ${ongoing}품목` : `거래 중 제약사 ${ongoing}곳`,
    // 주소가 없으면 헤더 [주소]를 비활성하고 이 문구를 보인다 (M-21)
    clinic.address ?? '등록된 주소가 없습니다',
  ].filter(Boolean) as string[];
  const toggle = (key: Exclude<HeaderOverlay, null>) => (open: boolean) =>
    setOverlay(open ? key : null);

  return (
    <Card>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <div
            className={cn(
              'flex size-[62px] shrink-0 items-center justify-center rounded-xl text-xs leading-[18px] font-medium',
              hospital
                ? 'bg-success-soft text-success'
                : 'bg-primary-soft text-primary',
            )}
          >
            {hospital ? '병원' : '의원'}
          </div>
          <div className="flex min-w-0 flex-col gap-[7px]">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg leading-7 font-bold text-ink lg:text-xl lg:leading-[30px]">
                {clinic.clinicName}
              </h2>
              <Badge tone={hospital ? 'success' : 'primary'}>
                {clinic.clinicTypeLabel}
              </Badge>
              <Badge tone={getStatusBadgeTone(clinic.statusView)}>
                {clinic.statusView}
              </Badge>
            </div>
            <p className="flex flex-wrap items-center gap-x-2 text-xs leading-[18px] text-ink-5">
              {meta.map((m, i) => (
                <span key={m} className="flex items-center gap-2">
                  {i > 0 && <span className="text-ink-faint">·</span>}
                  {m}
                </span>
              ))}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="navy"
            className="h-[38px] w-[84px] flex-col gap-0 rounded-md px-0 leading-none"
            onClick={() => setOverlay('pharmacy')}
          >
            <span className="text-xs leading-[18px] font-medium">문전약국</span>
            <span className="text-[10px] leading-[15px] font-normal text-navy-ink">
              {pharmacies.length}곳 · 조회
            </span>
          </Button>
          {/* 전화는 화면 없이 번호만 복사한다. 데스크톱에서는 tel:이 열리지 않는 환경이 많다 */}
          <Button
            variant="secondary"
            className="h-[38px] w-16 rounded-md text-xs"
            disabled={!clinic.phoneNumber}
            title={clinic.phoneNumber ? undefined : '등록된 전화번호가 없어요'}
            onClick={() =>
              void copyWithToast(
                clinic.phoneNumber!,
                '번호가 복사되었어요.',
                '번호를 복사하지 못했어요.',
              )
            }
          >
            전화
          </Button>
          <Button
            variant="secondary"
            className="h-[38px] w-16 rounded-md text-xs"
            disabled={!clinic.address}
            title={clinic.address ? undefined : '등록된 주소가 없습니다'}
            onClick={() => setOverlay('address')}
          >
            주소
          </Button>
          <Button
            variant="secondary"
            className="h-[38px] w-[104px] rounded-md text-xs"
            disabled={!clinic.businessLicenseImageUrl}
            title={
              clinic.businessLicenseImageUrl
                ? undefined
                : '등록된 사업자등록증이 없어요'
            }
            onClick={() => setOverlay('license')}
          >
            사업자등록증
          </Button>
        </div>
      </div>

      <PharmacyDialog
        open={overlay === 'pharmacy'}
        onOpenChange={toggle('pharmacy')}
        pharmacies={pharmacies}
      />
      {clinic.address && (
        <AddressDialog
          open={overlay === 'address'}
          onOpenChange={toggle('address')}
          clinicName={clinic.clinicName}
          address={clinic.address}
        />
      )}
      {clinic.businessLicenseImageUrl && (
        <BusinessLicenseDialog
          open={overlay === 'license'}
          onOpenChange={toggle('license')}
          clinicName={clinic.clinicName}
          imageUrl={clinic.businessLicenseImageUrl}
          onReload={onReload}
        />
      )}
    </Card>
  );
};

/**
 * 처방 등록 배너 (라임 CTA) — 웹 1차의 유일한 쓰기 진입점.
 * 거래 종료 · 거절은 숨기지 않고 회색 비활성 + 사유 문구 (M-07 §5).
 * 거래 신청 · 확인 중은 누르면 호스트가 "거래 승인 후…" 팝업을 띄운다.
 */
const RegisterBanner = ({
  detail,
  uploadRegistrationYm,
  uploadYearMonth,
}: {
  detail: BusinessRelationshipDetail;
  uploadRegistrationYm: string;
  uploadYearMonth: string;
}) => {
  const openUpload = useUIStore((s) => s.openPrescriptionUpload);
  const clinic = detail;
  const closed = getRelationshipGroup(clinic.statusView) === 'closed';
  // 처방 내역 카드의 기본 연도와 같은 쿼리(캐시 공유)
  const registrations = usePrescriptionRegistrations(
    detail.relationshipId,
    Number(uploadRegistrationYm.slice(0, 4)),
  );

  // 이번에 등록하면 들어갈 (등록월, 정산월)의 현재 장수
  const count = (registrations.data?.items ?? [])
    .filter(
      (r) =>
        r.registrationYearMonth === uploadRegistrationYm &&
        r.settlementYearMonth === uploadYearMonth,
    )
    .reduce((sum, r) => sum + r.imageCount, 0);
  const target = {
    relationshipId: detail.relationshipId,
    clinicName: clinic.clinicName,
    statusView: clinic.statusView,
  };

  if (closed) {
    return (
      <div className="flex flex-col gap-4 rounded-card bg-disabled py-5 pr-5 pl-6 sm:flex-row sm:items-center">
        <div className="flex flex-col gap-1">
          <p className="text-base leading-6 font-bold text-ink-6">처방 등록</p>
          <p className="text-[13px] leading-5 text-ink-6">
            {clinic.statusView === '신청 거절'
              ? '거래 신청이 거절되어 등록할 수 없어요'
              : '거래가 종료되어 등록할 수 없어요'}
          </p>
        </div>
        {/* 숨기지 않고 비활성으로 두되, 누르면 "등록할 수 없어요" 팝업을 띄운다 (M-07 §5).
            팝업 판정은 PrescriptionUploadHost가 statusView로 한다 */}
        <Button
          aria-disabled="true"
          onClick={() => openUpload(target)}
          className="w-full cursor-default bg-muted text-ink-disabled opacity-100 hover:bg-muted sm:ml-auto sm:w-28"
        >
          등록하기
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 rounded-card bg-lime py-5 pr-5 pl-6 sm:flex-row sm:items-center">
      <div className="flex flex-col gap-1">
        <p className="text-base leading-6 font-bold text-ink">처방 등록</p>
        <p className="text-[13px] leading-5 text-lime-ink">
          {count === 0
            ? `잊지말고 매 월 등록하세요. ${formatShortYm(uploadYearMonth)} 정산분이 아직 0장입니다.`
            : `${formatShortYm(uploadYearMonth)} 정산분 ${count}장이 등록되어 있어요. 빠진 처방전이 있으면 더 등록해 주세요.`}
        </p>
      </div>
      <Button
        className="w-full sm:ml-auto sm:w-28"
        onClick={() => openUpload(target)}
      >
        등록하기
      </Button>
    </div>
  );
};

/** M-04 · M-05 우측 상세 — 의원급과 병원급은 같은 컴포넌트에서 단위 라벨만 바꾼다 */
export const AccountDetail = ({
  relationshipId,
}: {
  relationshipId: number;
}) => {
  const { data, isLoading, isError, refetch } =
    useBusinessRelationshipDetail(relationshipId);
  // 연도 선택 범위 · 종료 거래처 기본 연도 — 목록 카드 값을 쓴다 (목록 캐시 공유)
  const listItem = useBusinessRelationshipList().data?.items.find(
    (i) => i.relationshipId === relationshipId,
  );
  // 오늘 올리면 들어갈 등록월 · 정산월. 서버도 같은 10일 마감 규칙으로 정한다
  const uploadRegistrationYm = useMemo(
    () => getUploadRegistrationYearMonth(),
    [],
  );
  const uploadYearMonth = useMemo(() => getUploadSettlementYearMonth(), []);
  /** M-19 · M-20 거래 정보 드로어가 열 섹션. null이면 닫힘 */
  const [tradeTab, setTradeTab] = useState<RelationshipGroup | null>(null);
  const [missedOpen, setMissedOpen] = useState(false);
  const isHospital = data?.tradeUnit === 'DRUG';

  return (
    <div className="flex flex-col gap-4">
      <Link
        to={ROUTE_PATHS.ACCOUNTS}
        className="flex w-fit items-center gap-1 text-[13px] text-ink-4 lg:hidden"
      >
        <ChevronLeft className="size-4" />
        목록
      </Link>
      {isLoading && (
        <>
          <LoadingBlock className="h-[108px]" />
          <LoadingBlock className="h-[88px]" />
          <LoadingBlock className="h-[352px]" />
        </>
      )}
      {isError && (
        <Card>
          <ErrorState onRetry={() => refetch()} />
        </Card>
      )}
      {data && (
        <>
          <ClinicHeader detail={data} onReload={() => void refetch()} />
          <RegisterBanner
            detail={data}
            uploadRegistrationYm={uploadRegistrationYm}
            uploadYearMonth={uploadYearMonth}
          />
          <PrescriptionHistoryCard
            key={data.relationshipId}
            relationshipId={data.relationshipId}
            clinicId={data.clinicId}
            currentRegistrationYm={uploadRegistrationYm}
            latestApprovalDate={listItem?.latestApprovalDate ?? null}
            endedAt={
              getRelationshipGroup(data.statusView) === 'closed'
                ? data.endedAt
                : null
            }
          />
          <TradeInfoCard
            isHospital={isHospital}
            counts={data.tradeStatusCounts}
            untradedApplicableCount={data.untradedApplicableCount}
            onOpenTrade={setTradeTab}
            onOpenMissed={() => setMissedOpen(true)}
          />
          <TradeInfoDrawer
            detail={data}
            isHospital={isHospital}
            section={tradeTab}
            onClose={() => setTradeTab(null)}
          />
          <MissedDrugsDrawer
            detail={data}
            open={missedOpen}
            onClose={() => setMissedOpen(false)}
          />
        </>
      )}
    </div>
  );
};
